// The device status string, and how many channels it carries.
//
//     [<UniqueNumber>]<v1>,<v2>,…,<vN>#<source>
//     [083A8D406BE4]1,0,1,0#BLE
//
// The SAME format on MQTT and BLE — one transport does not get its own dialect.
//
// ⚠️ CHANNEL COUNT IS PER PRODUCT. The spec says in bold: parse dynamically,
// never hardcode 4. Two shapes break a naive split:
//   • DeviceTypes 53/55/60 use `1-<v>` — a DASH, not a comma list
//   • 7+1 and 7+2 end in FAN SPEEDS, which are 10/20/…/100, not booleans, so
//     treating them as on/off makes a fan at speed 20 read as "on" and a fan at
//     10 (off) read as… also on, since any non-zero is truthy

// DeviceType (= DeviceID on the backend record) -> channel count.
export const CHANNELS_BY_TYPE = {
  53: 1, 55: 1, 60: 1,   // 1-touch / fan
  56: 2,                 // 2-touch
  91: 3,                 // Dices Magnet
  49: 4,                 // 4-touch
  1: 5, 92: 5,           // 4+1
  93: 8,                 // 7+1  — last 1 is a fan speed
  87: 9,                 // 7+2 Glass Touch — last 2 are fan speeds
};

// Types whose status is `1-<v>` rather than a comma list.
export const DASH_FORMAT_TYPES = [53, 55, 60];

// How many of the trailing values are fan speeds rather than booleans.
export const FAN_CHANNELS_BY_TYPE = { 93: 1, 87: 2 };

// Fan speed ladder: 10 is OFF, then the running speeds.
export const FAN_OFF = 10;
export const FAN_SPEEDS = [20, 30, 40, 50, 60, 70, 80, 100];

export const channelsFor = (deviceType) => CHANNELS_BY_TYPE[Number(deviceType)] ?? null;
export const usesDashFormat = (deviceType) => DASH_FORMAT_TYPES.includes(Number(deviceType));
export const fanChannelsFor = (deviceType) => FAN_CHANNELS_BY_TYPE[Number(deviceType)] ?? 0;

/**
 * Parse a status string.
 *
 * -> { uniqueNumber, values: number[], source, raw } or null.
 *
 * `values` are the raw numbers, NOT booleans — a fan channel's 40 means speed
 * 40, and flattening that to `true` loses the only information it carries.
 * Use isOn()/fanSpeed() to interpret a channel in the light of its DeviceType.
 */
export function parseStatus(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return null;

  const open = text.indexOf('[');
  const close = text.indexOf(']');
  if (open === -1 || close === -1 || close < open) return null;

  const uniqueNumber = text.slice(open + 1, close);
  let body = text.slice(close + 1);

  // `#<source>` — absent on some frames, so its absence is not a parse failure.
  let source = '';
  const hash = body.indexOf('#');
  if (hash !== -1) {
    source = body.slice(hash + 1).trim();
    body = body.slice(0, hash);
  }
  body = body.trim();
  if (!body) return null;

  // `1-<v>` (single channel) vs `a,b,c`. Detected from the STRING rather than
  // from the DeviceType, so a frame parses correctly even when the type is not
  // to hand — the two shapes cannot be confused.
  let values;
  const dash = /^(\d+)-(\d+)$/.exec(body);
  if (dash) {
    values = [Number(dash[2])];
  } else {
    values = body.split(',').map((v) => Number(String(v).trim()));
  }
  if (values.some((v) => !Number.isFinite(v))) return null;

  return { uniqueNumber, values, source, raw: text };
}

/** Whether channel `i` is a fan speed rather than an on/off switch. */
export function isFanChannel(deviceType, index, totalChannels) {
  const fans = fanChannelsFor(deviceType);
  if (!fans) return false;
  const total = totalChannels ?? channelsFor(deviceType) ?? 0;
  return index >= total - fans;
}

/** On/off for a switch channel — and for a fan, "not at the off step". */
export function isOn(value, deviceType, index, totalChannels) {
  if (isFanChannel(deviceType, index, totalChannels)) return Number(value) > FAN_OFF;
  return Number(value) === 1;
}

/** A fan channel's speed, or null when the channel is not a fan. */
export function fanSpeed(value, deviceType, index, totalChannels) {
  if (!isFanChannel(deviceType, index, totalChannels)) return null;
  const v = Number(value);
  return v === FAN_OFF ? 0 : v;
}

// ── Who caused the change (§4) ─────────────────────────────────────────────
// `#Touch` is how a real press is told apart from an echo of our own command —
// which is what keeps optimistic UI honest.
export const SOURCE_TOUCH = 'Touch';
export const SOURCE_BLE = 'BLE';
export const SOURCE_BOOT = 'Boot';

export const isPhysicalPress = (source) =>
  String(source || '').toLowerCase() === SOURCE_TOUCH.toLowerCase();
